import { apiService } from './api'

class SubscriptionMonitorService {
  constructor() {
    this.checkInterval = null
    this.isChecking = false
    this.lastCheckTime = null
    this.checkIntervalMs = 2 * 60 * 1000 // Check every 2 minutes
    this.listeners = []
  }

  // Start periodic subscription monitoring
  start() {
    if (this.checkInterval) {
      console.log('Subscription monitoring already started')
      return
    }

    console.log('Starting subscription monitoring...')
    
    // Initial check
    this.performCheck()
    
    // Set up periodic checks
    this.checkInterval = setInterval(() => {
      this.performCheck()
    }, this.checkIntervalMs)
  }

  // Stop monitoring
  stop() {
    if (this.checkInterval) {
      clearInterval(this.checkInterval)
      this.checkInterval = null
      console.log('Subscription monitoring stopped')
    }
  }

  // Perform subscription status check
  async performCheck() {
    if (this.isChecking) {
      console.log('Subscription check already in progress, skipping...')
      return
    }

    this.isChecking = true
    this.lastCheckTime = new Date()

    try {
      console.log('Performing subscription status check...')
      const response = await apiService.checkSubscriptionStatus()
      
      // If we get here, subscription is active
      this.notifyListeners('active', response)
      
    } catch (error) {
      if (error.response?.status === 403) {
        const data = error.response.data
        
        if (data.subscription_status === 'suspended') {
          console.log('Subscription suspended detected via monitoring')
          this.notifyListeners('suspended', data)
          
          // Emit suspension event
          window.dispatchEvent(new CustomEvent('subscription-suspended', {
            detail: data
          }))
          
          // Clear subscription cache
          apiService.clearSubscriptionCache()
          
        } else if (data.subscription_status === 'expired') {
          console.log('Subscription expired detected via monitoring')
          this.notifyListeners('expired', data)
          
          // Emit expired event
          window.dispatchEvent(new CustomEvent('subscription-expired', {
            detail: data
          }))
        }
      } else {
        console.error('Subscription check failed:', error)
        this.notifyListeners('error', { error: error.message })
      }
    } finally {
      this.isChecking = false
    }
  }

  // Add listener for subscription status changes
  addListener(callback) {
    this.listeners.push(callback)
  }

  // Remove listener
  removeListener(callback) {
    const index = this.listeners.indexOf(callback)
    if (index > -1) {
      this.listeners.splice(index, 1)
    }
  }

  // Notify all listeners
  notifyListeners(status, data) {
    this.listeners.forEach(callback => {
      try {
        callback(status, data)
      } catch (error) {
        console.error('Error in subscription listener:', error)
      }
    })
  }

  // Force immediate check
  async forceCheck() {
    console.log('Forcing immediate subscription check...')
    await this.performCheck()
  }

  // Get monitoring status
  getStatus() {
    return {
      isRunning: !!this.checkInterval,
      isChecking: this.isChecking,
      lastCheckTime: this.lastCheckTime,
      checkIntervalMs: this.checkIntervalMs,
      listenersCount: this.listeners.length
    }
  }

  // Update check interval
  setCheckInterval(intervalMs) {
    this.checkIntervalMs = intervalMs
    
    if (this.checkInterval) {
      // Restart with new interval
      this.stop()
      this.start()
    }
  }
}

// Create and export singleton instance
export const subscriptionMonitor = new SubscriptionMonitorService()

// Export class for testing
export { SubscriptionMonitorService }